import React, { memo } from 'react'

import { classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { arrayOf, bool, func, number, objectOf, oneOfType, shape, string } from 'prop-types'

import DesktopOnlyTooltip from 'Pods/Shared/DesktopOnlyTooltip/DesktopOnlyTooltip'
import { VARIATION_TYPES } from 'Pods/ThreeDStudio/constants'
import useOpenVariation from 'Pods/ThreeDStudio/Hooks/useOpenVariation'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import useVariationMenu from 'Pods/ThreeDStudio/Hooks/useVariationMenu'
import DeleteConfirmModal from 'Pods/ThreeDStudio/Shared/DeleteConfirmModal/DeleteConfirmModal'
import Lines from 'Pods/ThreeDStudio/Shared/VariationInfo/Components/Lines/Lines'
import useVariationDetails from 'Pods/ThreeDStudio/Shared/VariationInfo/useVariationDetails'
import getSourceItemUrl from 'Pods/ThreeDStudio/Shared/VariationMenu/getSourceItemUrl'
import VariationMenu from 'Pods/ThreeDStudio/Shared/VariationMenu/VariationMenu'

import Details from './Components/Details/Details'
import Thumbnail from './Components/Thumbnail/Thumbnail'
import { placeTooltipBesideTree } from './placeTooltipBesideTree'

function TreeRow({
  variation,
  position = null,
  isActive = false,
  isMuted = false,
  isActionLoading,
  onSetAsMain,
  onDelete,
}) {
  const t = useTranslation()
  const { id, slug, providerModelId, outputAssets, title, prompt, createdAt } = variation
  const sourceItemUrl = getSourceItemUrl(variation)
  const variationType = VARIATION_TYPES[providerModelId]
  const itemName = variationType?.label ?? title ?? prompt
  const { handleClick, handleKeyDown } = useOpenVariation({ id, slug, title: title ?? prompt })
  const menu = useVariationMenu({ id, itemName, onSetAsMain, onDelete })
  const { lines } = useVariationDetails(variation)
  const hasDetails = lines.length > 0
  const tooltipId = `variation-info-${id}`

  return (
    <>
      <DesktopOnlyTooltip
        id={tooltipId}
        content={hasDetails ? <Lines lines={lines} /> : null}
        place="top"
        containerClass="variations-tree__tooltip-anchor"
        className="variations-tree__tooltip-trigger"
        tooltipClassName="variations-tree__tooltip"
        overridePosition={placeTooltipBesideTree}
        focusWithin
      >
        <div
          className={classnames(
            'variations-tree__row',
            isActive && 'variations-tree__row--active',
            isMuted && 'variations-tree__row--muted',
            menu.isMenuOpen && 'variations-tree__row--menu-open',
          )}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          role="button"
          tabIndex={0}
          aria-current={isActive || undefined}
          aria-describedby={hasDetails ? `tooltip-${tooltipId}` : undefined}
        >
          <Thumbnail
            previewUrl={outputAssets?.[0]?.previewUrl}
            alt={title ?? prompt ?? variationType?.label ?? t('viewerSidebar.enhance.variations.variationPreview')}
            position={position}
          />
          <Details lines={lines} variationType={variationType} />
          <VariationMenu
            className="variations-tree__menu-trigger"
            createdAt={createdAt}
            sourceItemUrl={sourceItemUrl}
            isDisabled={isActionLoading}
            shouldClose={menu.shouldCloseDropdown}
            setShouldClose={menu.setShouldCloseDropdown}
            onOpenChange={menu.setIsMenuOpen}
            onSetAsMain={menu.handleSetAsMainClick}
            onDelete={menu.handleOpenDelete}
          />
        </div>
      </DesktopOnlyTooltip>
      {menu.isDeleteModalOpen && (
        <DeleteConfirmModal
          itemType="variation"
          itemName={itemName}
          isSubmitDisabled={isActionLoading}
          onSubmit={menu.handleConfirmDelete}
          onCancel={menu.handleCloseDelete}
        />
      )}
    </>
  )
}

TreeRow.propTypes = {
  variation: shape({
    id: oneOfType([string, number]).isRequired,
    slug: string,
    providerModelId: string,
    outputAssets: arrayOf(shape({
      previewUrl: string,
    })),
    title: string,
    prompt: string,
    createdAt: string,
    inputAssets: arrayOf(shape({
      id: number,
    })),
    sourceItems: objectOf(shape({
      url: string,
    })),
  }).isRequired,
  position: number,
  isActive: bool,
  isMuted: bool,
  isActionLoading: bool.isRequired,
  onSetAsMain: func.isRequired,
  onDelete: func.isRequired,
}

export default memo(TreeRow)
